<template>
  <div class="multi-selection-bar">
    <div class="edit-row">
      <div class="edit-row__label">{{ selectedCount }} {{ t('multiSelection.selected') }}</div>
      <div class="quick-control">
        <div
          class="quick-control__item"
          :class="{ active: optionActive === 'TEMPLATE' }"
          @click="optionToggle('TEMPLATE')"
        >
          <i class="fas fa-th"></i>
        </div>
        <div
          class="quick-control__item"
          :class="{ active: optionActive === 'VISIBILITY' }"
          @click="optionToggle('VISIBILITY')"
        >
          <i class="fas fa-low-vision"></i>
        </div>
        <div
          class="quick-control__item"
          @click="deselectAll"
        >
          <i class="fas fa-object-ungroup"></i>
        </div>
        <div
          class="quick-control__item"
          :class="{ blocked: loading }"
          @click="deleteSelected"
        >
          <i class="fas fa-trash"></i>
        </div>
      </div>
    </div>
    <multi-selection-template v-if="optionActive === 'TEMPLATE'" />
    <multi-selection-visibility v-if="optionActive === 'VISIBILITY'" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapArea } from '@/modules/map-editor';
import MultiSelectionTemplate from './modules/MultiSelectionTemplate.vue';
import MultiSelectionVisibility from './modules/MultiSelectionVisibility.vue';

/**
 * Edit bar displayed when multiple nodes are selected.
 * Provides bulk actions: delete all selected, deselect all, template, visibility.
 */

const store = useStore();
const { t } = useI18n();
const { mapAreaService } = useMapArea();

const loading = ref(false);
const optionActive = ref<string | null>(null);

function optionToggle(option: string): void {
  optionActive.value = optionActive.value === option ? null : option;
}

const selectedMap = computed(() => store.getters['nodes/getSelectedNodes'] as Map<number, unknown>);
const selectedCount = computed(() => store.getters['nodes/getSelectedCount'] as number);
const nodes = computed(() => store.getters['nodes/getNodes']);
const currentLayerId = computed(() => store.getters['layers/getCurrentLayerId']);

/**
 * Delete all currently selected nodes.
 * Iterates each selected node and dispatches removal.
 */
async function deleteSelected(): Promise<void> {
  if (loading.value) return;
  loading.value = true;
  const selectedIds = Array.from(selectedMap.value.keys());

  for (const nodeId of selectedIds) {
    await store.dispatch('nodes/removeNodeRequest', nodeId);
    await store.dispatch('nodes/removeNode', nodeId);
  }

  await store.dispatch('nodes/deselectAllNodes');
  mapAreaService.renderFrame();
  loading.value = false;
}

/**
 * Deselect all currently selected nodes and clear visual selection state.
 */
async function deselectAll(): Promise<void> {
  const allNodes = nodes.value;
  const layerId = currentLayerId.value;

  allNodes.forEach((node: { selected: boolean; setSelected: (s: boolean) => void; cacheInstanceSetDimensions: () => void; cache: (l: number) => void; id: number }) => {
    if (node.selected) {
      node.setSelected(false);
      node.cacheInstanceSetDimensions();
      node.cache(layerId);
      mapAreaService.updateElement(true, node.id);
    }
  });

  await store.dispatch('nodes/deselectAllNodes');
  await store.dispatch('dashboardOptions/groupNodeToggle', false);
  mapAreaService.renderFrame();
}
</script>

<style lang="scss" scoped>
.multi-selection-bar {
  position: fixed;
  bottom: 140px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column-reverse;
}

.multi-selection-bar > .edit-row {
  background: $black;
}
</style>
